import React, { FC, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';

import { useDeepCompareEffect, useDeepCompareMemo } from '@clatter/platform';
import { config } from '../../../../config';
import { addNotification } from '../../../../store';
import { ProjectPageLayout } from '../../../../layouts';
import { getLocalStorageAccessToken } from '../../../../helpers/auth';
import {
  useGetProjectTemplateByIdQuery,
  useUpdateDocumentMutation,
  useUpdateProjectMutation,
} from '../../../../services';

import { useWorkspace } from '../../../../context/WorkspaceProvider';
import { MicrositesPublishSection } from '../components/MicrositesPublishSection';
import { GenerateDocumentsSection } from '../components/GenerateDocumentsSection';
import { FormButtons, StackContainer, DynamicFormField, FormInputWrapper, NgCard, NgCardContent } from '../../../UI';

interface ValuesPageProps {
  projectData?: any;
  isLoading: boolean;
  onAction: any;
}

type WorkspaceFormValues = {
  selectedDocumentTemplates: GenerateDocumentTemplateItem[];
};

const Step = styled.div`
  background-color: #f7f7f7;
  border: 2px solid rgb(42, 125, 167);

  h2 {
    background-color: #282828;
    line-height: 1.6;
    display: flex;
    gap: 16px;
    color: #fff;
  }

  h2 span:first-child {
    font-size: 14px;
    background-color: rgb(42, 125, 167);
    color: #fff;
    display: flex;
    align-items: center;
    padding: 0 8px;
  }

  > div {
    padding: 0 8px;
  }
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  align-items: start;
  grid-gap: ${({ theme }) => theme.spacing.medium};

  .column {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.medium};
  }

  @media (max-width: 1600px) {
    grid-template-columns: 1fr;
  }

  .tabs-container {
    display: grid;
    overflow: hidden;
  }

  .tabs {
    display: flex;
    gap: 12px;
    flex-wrap: nowrap;
    overflow-x: scroll;
    margin-bottom: 12px;
    padding: 4px 0;
  }

  .tabs .active {
    background-color: #777;
    color: #fff;
  }

  .tabs > * {
    flex-shrink: 0;
  }
`;

const workspaceFormSchema = {
  type: 'object',
  properties: {
    selectedDocumentTemplates: {
      type: 'array',
      minLength: 1,
      errorMessage: {
        minLength: 'Selected document templates can not be empty.',
      },
    },
  },
  required: ['selectedDocumentTemplates'],
};

const isFieldRequired = (requiredFields: string[], fieldName: string) => {
  return (requiredFields || []).includes(fieldName);
};

const hasVisibleInputs = (fields: DynamicFormField[]): boolean =>
  fields.some((f) => {
    if (f.inputType === 'hidden') return false;
    if ('items' in f && Array.isArray(f.items) && f.items.length > 0) {
      return hasVisibleInputs(f.items);
    }
    return true;
  });

const getUnifiedDataFromProjectValues = ({
  values,
  query: gqlQuery,
  schema = { properties: {}, required: [], type: 'object' },
}: {
  values: any;
  schema?: JSONSchema;
  query?: string;
}) => {
  const initialValues: { [key: string]: string | number | null | string[] } = { ...values };

  const createFormFields = (schema: any): DynamicFormField[] =>
    Object.keys(schema.properties ?? {})
      //@ts-ignore
      .map<DynamicFormField>((keyName) => {
        const schemaFieldData = schema.properties[keyName];

        const inputType: DynamicFormField['inputType'] = schemaFieldData?.inputType || 'text';
        // @todo support if an input may handle different types
        // const type: DynamicFormField['type'] = schemaFieldData?.type || 'string';

        const fieldData: BaseDynamicFormField = {
          name: keyName,
          order: schemaFieldData.inputOrder || schemaFieldData?.inputOptions?.order || 0,
          required: isFieldRequired(schema?.required || [], keyName),
          label: schemaFieldData?.displayName || schemaFieldData?.title || keyName,
        };

        switch (inputType) {
          case 'text':
          case 'textarea':
            return {
              ...fieldData,
              type: 'string',
              inputType,
              inputOptions: {
                defaultValue: schemaFieldData?.inputOptions?.defaultValue,
              },
            };

          case 'number': {
            return {
              ...fieldData,
              type: 'number',
              inputType,
            };
          }

          //@ts-ignore
          case 'pages-picker':
            return {
              ...fieldData,
              type: 'array', // Make sure type is array
              inputType,
              inputOptions: schemaFieldData?.inputOptions || {},
            };

          case 'video-picker':
          case 'image-picker':
            return {
              ...fieldData,
              type: 'number',
              inputType,
              required: false,
              inputOptions: {
                sourcePath: schemaFieldData.inputOptions?.sourcePath || schemaFieldData.sourcePath,
                pickerVariant: schemaFieldData.inputOptions?.pickerVariant || 'default',
              },
            };

          case 'checkbox':
            return {
              ...fieldData,
              type: 'boolean',
              inputType,
              inputOptions: {},
            };

          case 'checkbox-group':
            return {
              ...fieldData,
              type: 'string',
              inputType,
              inputOptions: {
                checkboxOptions: schemaFieldData?.inputOptions?.checkboxOptions || [],
              },
            };

          case 'select':
            return {
              ...fieldData,
              type: 'string',
              inputType,
              inputOptions: {
                selectOptions: schemaFieldData?.inputOptions?.selectOptions || [],
              },
            };

          case 'ordered-list':
            return {
              ...fieldData,
              type: 'array',
              items: createFormFields(schemaFieldData.items ?? {}),
              inputType,
              minItems: schemaFieldData.minItems,
              maxItems: schemaFieldData.maxItems,
              inputOptions: {
                gqlQuery: schemaFieldData?.inputOptions?.gqlQuery || gqlQuery,
                gqlQueryKey: schemaFieldData?.inputOptions?.gqlQueryKey,
                previewComponent: schemaFieldData?.inputOptions?.previewComponent,
                variant: schemaFieldData?.inputOptions?.variant ?? 'default',
                filterType: schemaFieldData?.inputOptions?.filterType || undefined,
              },
            };

          case 'queryKey':
            return {
              ...fieldData,
              type: 'array',
              items: createFormFields(schemaFieldData.items ?? {}),
              inputType,
              inputOptions: {
                gqlQueryKey: schemaFieldData?.inputOptions?.gqlQueryKey,
              },
            };

          case 'object':
            return {
              ...fieldData,
              type: 'object',
              items: createFormFields(schemaFieldData),
              inputType,
            };

          default:
            return {
              ...fieldData,
              type: 'string',
              inputType,
            };
        }
      })
      .sort((a, b) => a.order - b.order);

  // Step 2) map schema to form fields
  const allFormFields = createFormFields(schema).sort((a, b) => a.order - b.order);

  // exclude queryKey fields from the form UI — they are auto-populated from GQL query results
  // also remove object fields that end up with no visible children
  const filterListingFields = (fields: DynamicFormField[]): DynamicFormField[] =>
    fields
      .filter((field) => field.inputType !== 'queryKey')
      .map((field) => ('items' in field && Array.isArray(field.items) ? { ...field, items: filterListingFields(field.items) } : field))
      .filter((field) => !('items' in field && Array.isArray(field.items) && field.items.length === 0));

  const formFields = filterListingFields(allFormFields);

  const steps: DynamicFormData['steps'] = [];
  for (const field of formFields) {
    if (field.inputType === 'object') {
      steps.push({
        name: field.label,
        inputs: [field],
      });
    } else {
      // steps[0] ??= { name: 'Provide details', inputs: [] };
      // steps[0].inputs.push(field);
    }
  }
  // for (const value of values || []) {
  //   const valueSteps = (value?.schema?.steps ?? []).map((step: { name: string; inputs: string[] }) => {
  //     return {
  //       name: step.name,
  //       inputs: step.inputs.map((i) => formFields.find((f) => f.name === i)).filter(Boolean) as DynamicFormField[],
  //     };
  //   });

  //   steps.push(...valueSteps);
  // }

  if (steps.length === 0) {
    steps.push({
      name: 'Provide details',
      inputs: formFields,
    });
  }

  // Step 3) set initial values if not provided from api in Step 1
  const createInitialValues = (formFields: DynamicFormField[], initialValues: Record<string, any>) => {
    for (const formField of formFields) {
      if (initialValues[formField.name] !== undefined && initialValues[formField.name] !== null) continue;

      switch (formField.inputType) {
        case 'checkbox':
          initialValues[formField.name] = false;
          break;

        case 'checkbox-group':
          initialValues[formField.name] = [];
          break;

        case 'ordered-list':
          initialValues[formField.name] = [];
          break;

        case 'pages-picker':
          initialValues[formField.name] = [];
          break;

        case 'object':
          initialValues[formField.name] = {};
          createInitialValues(formField.items, initialValues[formField.name]);
          break;

        case 'select':
          initialValues[formField.name] = null;
          break;

        case 'video-picker':
        case 'image-picker':
          initialValues[formField.name] = null;
          // initialValues[formField.name] = '';
          break;

        case 'text':
        case 'textarea':
          initialValues[formField.name] = formField?.inputOptions?.defaultValue || '';
          break;

        case 'number':
          initialValues[formField.name] = 0;
          break;

        default:
          initialValues[formField.name] = '';
          break;
      }
    }
  };

  createInitialValues(formFields, initialValues);

  // Modify schema to allow null values for image-picker and video-picker fields
  // This needs to handle nested schemas (objects within objects)
  const modifySchemaForImagePickers = (schemaObj: any, fields: DynamicFormField[]): any => {
    if (!schemaObj.properties) return schemaObj;

    const modifiedProperties = { ...schemaObj.properties };

    fields.forEach(field => {
      const fieldName = field.name;

      if (modifiedProperties[fieldName]) {
        if (field.inputType === 'image-picker' || field.inputType === 'video-picker') {
          const originalField = modifiedProperties[fieldName];
          const { type, ...restOfField } = originalField as any;
          modifiedProperties[fieldName] = {
            ...restOfField,
            // @ts-ignore - Using anyOf to allow multiple types
            anyOf: [
              { type: 'number' },
              { type: 'null' },
              { type: 'string', maxLength: 0 } // Allow empty string
            ]
          };
        } else if (field.inputType === 'object' && field.items) {
          // recursively handle nested objects
          modifiedProperties[fieldName] = modifySchemaForImagePickers(
            modifiedProperties[fieldName],
            field.items
          );
        }
      }
    });

    return {
      ...schemaObj,
      properties: modifiedProperties,
    };
  };

  const modifiedSchema = modifySchemaForImagePickers(schema, formFields);

  return { schema: modifiedSchema, initialValues, formFields, steps: steps };
};

export const ValuesPage: FC<ValuesPageProps> = ({ projectData, onAction, isLoading: isParentLoading }) => {
  const dispatch = useDispatch();
  const { setLongPullItemId } = useWorkspace();

  const { data: workspaceTemplateData, isLoading: isWorkspaceTemplateDataLoading } = useGetProjectTemplateByIdQuery(
    {
      workspaceId: projectData?.ProjectTemplate?.ID,
    },
    { skip: !projectData?.ProjectTemplate?.ID },
  );
  const [updateDocument, { isLoading: isUpdateDocumentLoading }] = useUpdateDocumentMutation();
  const [updateProject, { isLoading: isUpdateProjectLoading }] = useUpdateProjectMutation();
  const isDataBrowserEnabled = projectData?.Metadata?.features?.enableDataBrowser;

  const isLoading = isParentLoading || isUpdateDocumentLoading || isUpdateProjectLoading;

  const [tab, setTab] = useState<string | null>();
  const tabs: { label: string; value: string }[] = useDeepCompareMemo(() => {
    if (!workspaceTemplateData) return [];

    const template = workspaceTemplateData.DocumentTemplates.find((t) => t.OutputType === 'html')?.Template;
    if (!template) return [];

    // @ts-ignore
    const pages: any[] = template.pages || [];
    if (pages.length === 0) return [];

    const pagesTabs = pages.map((page) => ({
      label: page?.displayName || page?.name || 'Unknown Page',
      value: page.name,
    }));

    if (workspaceTemplateData?.ValuesSchema?.properties) {
      return [{ label: 'Workspace Settings', value: 'default' }, ...pagesTabs];
    }

    return pagesTabs;
  }, [workspaceTemplateData]);

  const valuesSchemas: Record<string, DynamicFormData> = useDeepCompareMemo(() => {
    const project = projectData?.Values?.find((v: any) => v.name === 'project');
    const html = (workspaceTemplateData?.DocumentTemplates ?? []).find((v: any) => v.OutputType === 'html');

    //@todo this should be taken from document instance not project values
    const htmlData = projectData?.Values?.find((item: any) => item?.name === 'html');

    if (html) {
      const query =
        projectData.Documents.find((d: any) => d.DocumentTemplate.OutputType === 'html')?.DocumentTemplate.Query
          .Query || '';

      const htmlSchemas = Object.entries(html.ValuesSchema.properties ?? {}).reduce(
        (acc, [page, schema]) => {
          const valuesSchema = getUnifiedDataFromProjectValues({
            schema: schema as JSONSchema,
            values: htmlData.data?.[page] ?? {},
            query,
          });
          return { ...acc, [page]: valuesSchema };
        },
        {
          default: {},
        },
      );

      const projectValues = projectData?.Values?.find((item: any) => item?.name === 'project');

      const workspaceSettingsSchema = getUnifiedDataFromProjectValues({
        schema: projectData?.ProjectTemplate?.ValuesSchema ?? { type: 'object', properties: {}, required: [] },
        values: projectValues?.data ?? {},
      });

      if (workspaceSettingsSchema?.formFields?.length > 0) {
        htmlSchemas['default'] = workspaceSettingsSchema;
      }

      return htmlSchemas;
    } else {
      return {
        default: getUnifiedDataFromProjectValues({
          schema: projectData?.ProjectTemplate?.ValuesSchema ?? { type: 'object', properties: {}, required: [] },
          values: project?.data ?? {},
        }),
      };
    }
  }, [workspaceTemplateData, projectData]);

  const visibleTabs: { label: string; value: string }[] = useDeepCompareMemo(() => {
    return tabs.filter((t) => {
      const schema = valuesSchemas[t.value];
      if (!schema) return false;
      return hasVisibleInputs(schema.formFields || []);
    });
  }, [tabs, valuesSchemas]);

  useEffect(() => {
    if (visibleTabs.length === 0) return;
    setTab(visibleTabs[0].value);
  }, [visibleTabs]);

  const unifiedData = tab ? valuesSchemas[tab] : valuesSchemas['default'];

  const methods = useForm<any>({
    mode: 'onChange',
    // @ts-ignore TS2589: Type instantiation is excessively deep and possibly infinite.
    resolver: ajvResolver((unifiedData?.schema as any) || {}, {
      formats: fullFormats,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      strict: false, // allows custom props to be passed along schema
    }),
  });

  const {
    reset,
    getValues,
    handleSubmit,
    formState: { errors, isDirty, isValid: isFormValid },
  } = methods;

  const isValid = isFormValid;

  // workspace state form
  const workspaceFormMethods = useForm<WorkspaceFormValues>({
    mode: 'onChange',
    // @ts-ignore TS2589: Type instantiation is excessively deep and possibly infinite.
    resolver: ajvResolver(workspaceFormSchema || {}, {
      formats: fullFormats,
    }),
    defaultValues: {
      selectedDocumentTemplates: [],
    },
  });

  const onSubmit = async (data: any) => {
    reset(data);

    const html = projectData?.Documents?.find((d: any) => d.DocumentTemplate.OutputType === 'html');

    if (html) {
      const isEditingWorkspaceSettings = tab === 'default';
      const values = { ...getValues() };

      if (tab) {
        const page: Record<string, any> = {};

        for (const key in values) {
          page[key] = values[key];
          values[key] = undefined;
        }

        values[tab] = page;
      }

      if (isEditingWorkspaceSettings) {
        // update workspace
        try {
          await updateProject({
            projectId: projectData.ID,
            data: {
              values: {
                // ...html.Values,
                ...data,
              },
            },
          }).unwrap();
        } catch (error: any) {
          const msg = 'Failed to update workspace.';
          dispatch(
            addNotification({
              type: 'error',
              message: msg,
            }),
          );
          console.error(msg, error);
        }
      } else {
        // update document
        const updateResult: any = await updateDocument({
          documentId: html.ID,
          projectId: projectData.ID,
          data: {
            values: {
              ...html.Values,
              ...values,
            },
          },
        });

        if (updateResult?.data?.result !== 'success') {
          const message = 'Error updating document';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message);
          return;
        }
      }

      dispatch(
        addNotification({
          type: 'success',
          message: `Project updated successfully.`,
        }),
      );

      // let's clear generated
      // documents state on submit
      // so it will reenable generate button
      setLongPullItemId(null);
    } else {
      try {
        await updateProject({
          projectId: projectData.ID,
          data: {
            values: getValues(),
          },
        }).unwrap();
      } catch (error: any) {
        const msg = 'Failed to update workspace.';
        dispatch(
          addNotification({
            type: 'error',
            message: msg,
          }),
        );
        console.error(msg, error);
      }
    }
  };

  const queryGqlData = async (query: string) => {
    const response = await fetch(`${config.assetServiceBaseUrl}/v2/projects/${projectData?.ID}/graphql`, {
      method: 'POST',
      body: JSON.stringify({ query }),
      headers: {
        Authorization: `Bearer ${getLocalStorageAccessToken()}`,
      },
    });

    return await response.json();
  };

  // set the initial form state (new case)
  useDeepCompareEffect(() => {
    const values = getValues() || {};
    const initialValues = unifiedData?.initialValues || {};

    if (!Object.keys(values).length && Object.keys(initialValues).length) {
      reset(initialValues);
    }
  }, [unifiedData?.initialValues, getValues]);

  // set the initial form state using values from the project (edit case)
  useDeepCompareEffect(() => {
    if (unifiedData?.initialValues) {
      reset(unifiedData?.initialValues);
    }
  }, [unifiedData?.initialValues]);

  return (
    <ProjectPageLayout noWrapper>
      <StyledGrid>
        <NgCard>
          <NgCardContent>
            <div className="tabs-container">
              <div className="tabs">
                {visibleTabs.map((t) => (
                  <button
                    key={t.value}
                    className={t.value === tab ? 'active' : ''}
                    type="button"
                    disabled={isDirty}
                    onClick={() => setTab(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <StackContainer spacing={5}>
                  <StackContainer spacing={3} direction="column">
                    {unifiedData?.steps?.length === 0 &&
                      (unifiedData?.formFields || []).map((fieldData) => (
                          <FormInputWrapper
                            key={tab + fieldData.name}
                            label={fieldData.inputType === 'checkbox' ? undefined : fieldData.label}
                            required={fieldData.required}
                            errorMessage={(errors?.[fieldData.name]?.message as string) || null}
                            hidden={fieldData.inputType === 'hidden'}
                          >
                            <DynamicFormField data={fieldData} disabled={isLoading} />
                          </FormInputWrapper>
                        ))}

                    {unifiedData?.steps?.length !== 0 &&
                      (unifiedData?.steps || []).filter(({ inputs }) => hasVisibleInputs(inputs)).map(({ name, inputs }, idx) => (
                        <Step>
                          <h2>
                            <span>Step {idx + 1}</span>
                            <span>{name}</span>
                          </h2>

                          {inputs.map((fieldData) => {
                            const errorMessage = !!errors?.[fieldData.name] ? 'This field has errors.' : null;

                            // if (errorMessage) {
                            //   console.log('Errors: ', errors?.[fieldData.name]);
                            // }

                            return (
                              <FormInputWrapper
                                key={tab + name + fieldData.name}
                                label={fieldData.inputType === 'checkbox' || fieldData.inputType === 'object' ? undefined : fieldData.label}
                                required={fieldData.required}
                                errorMessage={errorMessage}
                                hidden={fieldData.inputType === 'hidden'}
                              >
                                <DynamicFormField data={fieldData} disabled={isLoading} queryGqlData={queryGqlData} />
                              </FormInputWrapper>
                            );
                          })}
                        </Step>
                      ))}
                  </StackContainer>

                  <FormButtons isLoading={isLoading} isDirty={isDirty} isValid={isValid} reset={reset} />
                </StackContainer>
              </form>
            </FormProvider>
          </NgCardContent>
        </NgCard>

        <div className="column">
          <FormProvider {...workspaceFormMethods}>
            <GenerateDocumentsSection
              projectData={projectData}
              workspaceTemplateData={workspaceTemplateData}
              isQueriesEnabled={isDataBrowserEnabled}
              isQueryValid={isValid && !isDirty}
              isLoading={isWorkspaceTemplateDataLoading}
            />

            <MicrositesPublishSection
              projectData={projectData}
              isQueryValid={isValid && !isDirty}
              isQueriesEnabled={isDataBrowserEnabled}
              isLoading={isLoading}
            />
          </FormProvider>
        </div>
      </StyledGrid>
    </ProjectPageLayout>
  );
};
